<template>
  <!-- <div > -->
  <div v-if="InterviewCourseStore.currentNodeStatus === 'recording'">
    <!-- 浮动球组件现在由外部通过v-show控制显示隐藏 -->
    
    <!-- 主录音播放器 -->
    <audio
      v-show="false"
      :src="AudioStore.audioUrl"
      controls
      class="main-audio-player"
    ></audio>
    
    <!-- 30秒切片录音播放器 -->
    <audio
      v-show="false"
      :src="sliceAudioUrl"
      controls
      class="slice-audio-player"
    ></audio>
    
    <!-- 60秒切片录音播放器 -->
    <audio
      v-show="false"
      :src="sliceAudioUrl60s"
      controls
      class="slice-audio-player-60s"
    ></audio>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { useRoute } from "vue-router";
import { interviewStore } from "~/modules/my-interview/store/interview-store.js";
import { interviewCourseStore } from '../store/interview-course-store.js';
import { audioStore } from '../store/audio-store.js';
import { getSupportedMimeType, getFileExtension } from '~/utils/audio-transcode.js';

let sliceTimer = null; // 30秒追问切片定时器（生成时间段切片：0-30s, 30-60s, 60-90s...）
let assessmentTimer = null; // 60秒考察维度完成情况定时器

const InterviewStore = interviewStore(); //面试store
const InterviewCourseStore = interviewCourseStore();
const AudioStore = audioStore();
const route = useRoute();

// 追问切片录音相关（30秒时间段切片）
const sliceMediaRecorder = ref(null);
const sliceAudioChunks = ref([]);
const sliceAudioBlob = ref(null);
const sliceAudioUrl = ref("");
const sliceStartTime = ref(0); // 记录当前切片的开始时间（相对于主录音开始的时间，单位：秒）

// 考察维度切片录音相关（60秒时间段切片）
const sliceMediaRecorder60s = ref(null);
const sliceAudioChunks60s = ref([]);
const sliceAudioBlob60s = ref(null);
const sliceAudioUrl60s = ref("");
const sliceStartTime60s = ref(0); // 记录当前60秒切片的开始时间（相对于主录音开始的时间，单位：秒）

// 注意：主录音的 dataavailable 收集由 audio-store 统一负责。
// FloatingBall 不再二次监听/二次 push，否则会导致 chunks 重复，音频异常/转写不稳定。

// ============ 追问切片录音（30秒时间段切片：0-30s, 30-60s, 60-90s...）============
// 启动追问切片录音 30s版（使用主录音的共享流）
const startSliceRecording = async () => {
  try {
    // 检查主录音状态
    if (!AudioStore.isRecording || AudioStore.isPaused) {
      console.warn("[FloatingBall] 主录音未在录制中，无法启动切片录音");
      return;
    }
    
    // 使用主录音的音频流（共享流，避免资源竞争）
    const stream = AudioStore.audioStream;
    
    if (!stream || stream.getTracks().length === 0) {
      console.error("[FloatingBall] 主录音流不可用");
      return;
    }
    
    // 检查流是否处于活动状态
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack || audioTrack.readyState !== 'live') {
      console.error("[FloatingBall] 主录音流未激活");
      return;
    }
    
    // 记录当前切片的开始时间（使用主录音的计时器）
    sliceStartTime.value = AudioStore.seconds;
    
    // 创建追问切片录音器（使用共享流）
    const mimeType = getSupportedMimeType();
    sliceMediaRecorder.value = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    sliceAudioChunks.value = [];
    
    // 添加事件监听器
    sliceMediaRecorder.value.addEventListener("dataavailable", (event) => {
      if (event.data && event.data.size > 0) {
        sliceAudioChunks.value.push(event.data);
        console.log("[FloatingBall-追问切片] 收集到音频数据，大小:", event.data.size, "字节");
      }
    });
    
    sliceMediaRecorder.value.addEventListener("stop", () => {
      // 计算切片的时间范围
      const sliceEndTime = AudioStore.seconds;
      const sliceDuration = sliceEndTime - sliceStartTime.value;
      
      // 创建切片音频Blob
      const actualMimeType = mimeType || sliceMediaRecorder.value?.mimeType || "";
      sliceAudioBlob.value = new Blob(sliceAudioChunks.value, actualMimeType ? { type: actualMimeType } : undefined);
      sliceAudioUrl.value = URL.createObjectURL(sliceAudioBlob.value);
      
      console.log(`[FloatingBall-追问切片] 切片录音完成，时间段: ${sliceStartTime.value}s-${sliceEndTime}s (${sliceDuration}s)，大小:`, sliceAudioBlob.value.size, "字节");
      
      // 上传切片并调用追问接口
      if (sliceAudioBlob.value.size > 0) {
        uploadSliceForQuestion(sliceAudioBlob.value);
      }
      
      // ⚠️ 关键：绝对不要关闭流！主录音还在使用
      // stream.getTracks().forEach(track => track.stop());  // ❌ 删除这行，避免破坏主录音
    });
    
    // 开始切片录音
    sliceMediaRecorder.value.start(1000); // 每1秒生成一次数据块，与主录音保持一致
    console.log(`[FloatingBall-追问切片] 切片录音已开始，开始时间: ${sliceStartTime.value}s`);
    
  } catch (error) {
    console.error("[FloatingBall-追问切片] 启动切片录音失败:", error);
  }
};

// 启动考察维度切片录音 60s版（使用主录音的共享流）
const startSliceRecording60s = async () => {
  try {
    // 检查主录音状态
    if (!AudioStore.isRecording || AudioStore.isPaused) {
      console.warn("[FloatingBall-60s] 主录音未在录制中，无法启动切片录音");
      return;
    }
    
    // 使用主录音的音频流（共享流，避免资源竞争）
    const stream = AudioStore.audioStream;
    
    if (!stream || stream.getTracks().length === 0) {
      console.error("[FloatingBall-60s] 主录音流不可用");
      return;
    }
    
    // 检查流是否处于活动状态
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack || audioTrack.readyState !== 'live') {
      console.error("[FloatingBall-60s] 主录音流未激活");
      return;
    }
    
    // 记录当前60秒切片的开始时间（使用主录音的计时器）
    sliceStartTime60s.value = AudioStore.seconds;
    
    // 创建考察维度切片录音器（使用共享流）
    const mimeType = getSupportedMimeType();
    sliceMediaRecorder60s.value = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    sliceAudioChunks60s.value = [];
    
    // 添加事件监听器
    sliceMediaRecorder60s.value.addEventListener("dataavailable", (event) => {
      if (event.data && event.data.size > 0) {
        sliceAudioChunks60s.value.push(event.data);
        console.log("[FloatingBall-60s切片] 收集到音频数据，大小:", event.data.size, "字节");
      }
    });
    
    sliceMediaRecorder60s.value.addEventListener("stop", () => {
      // 计算切片的时间范围
      const sliceEndTime = AudioStore.seconds;
      const sliceDuration = sliceEndTime - sliceStartTime60s.value;
      
      // 创建切片音频Blob
      const actualMimeType = mimeType || sliceMediaRecorder60s.value?.mimeType || "";
      sliceAudioBlob60s.value = new Blob(sliceAudioChunks60s.value, actualMimeType ? { type: actualMimeType } : undefined);
      sliceAudioUrl60s.value = URL.createObjectURL(sliceAudioBlob60s.value);
      
      console.log(`[FloatingBall-60s切片] 切片录音完成，时间段: ${sliceStartTime60s.value}s-${sliceEndTime}s (${sliceDuration}s)，大小:`, sliceAudioBlob60s.value.size, "字节");
      
      // 上传切片并调用考察维度完成情况接口
      if (sliceAudioBlob60s.value.size > 0) {
        uploadSliceForAssessmentSubPointCompletion(sliceAudioBlob60s.value);
      }
      
      // ⚠️ 关键：绝对不要关闭流！主录音还在使用
      // stream.getTracks().forEach(track => track.stop());  // ❌ 删除这行，避免破坏主录音
    });
    
    // 开始切片录音
    sliceMediaRecorder60s.value.start(1000); // 每1秒生成一次数据块，与主录音保持一致
    console.log(`[FloatingBall-60s切片] 切片录音已开始，开始时间: ${sliceStartTime60s.value}s`);
    
  } catch (error) {
    console.error("[FloatingBall-60s切片] 启动切片录音失败:", error);
  }
}
// 停止追问切片录音
const stopSliceRecording = () => {
  if (sliceMediaRecorder.value && sliceMediaRecorder.value.state === 'recording') {
    sliceMediaRecorder.value.stop();
    console.log("[FloatingBall-追问切片] 切片录音已停止");
  }
};

// 停止考察维度切片录音
const stopSliceRecording60s = () => {
  if (sliceMediaRecorder60s.value && sliceMediaRecorder60s.value.state === 'recording') {
    sliceMediaRecorder60s.value.stop();
    console.log("[FloatingBall-60s切片] 切片录音已停止");
  }
};

// ============ 上传切片并调用接口 ============
// 30s上传追问切片并调用追问接口
const uploadSliceForQuestion = async (blob) => {
  if (!blob) {
    console.error("[FloatingBall-追问切片] 没有可上传的录音切片");
    return;
  }
  
  try {
    const timestamp = Date.now();
    console.log('[FloatingBall-追问切片] 开始上传切片到OSS，大小:', blob.size, '字节');
    const ext = getFileExtension(blob.type || '');
    
    // 使用 audio-store 的上传方法，传入自定义文件名
    const uploadResult = await AudioStore._uploadToOSS({
      blob,
      processId: route.query.interviewId,
      nodeId: route.query.nodeId,
      interviewStore: InterviewStore,
      customFileName: `adviseRecording_${timestamp}.${ext}`
    });
    
    if (uploadResult.success) {
      console.log('[FloatingBall-追问切片] 切片上传成功，URL:', uploadResult.ossUrl);
      
      // 调用追问建议接口（不等待返回）
      const arr = InterviewCourseStore.suggestAbilityList.map(item => {
        return{
          ...item, 
        }
      })
      arr.forEach(item => {
        item.assessmentSubPoints.forEach(subItem => {
          subItem.assessmentStatusTitle = subItem.assessmentStatus === 0 ? '未考察' : subItem.assessmentStatus === 1 ? '考察中' : '考察完成';
        })
      })
      const data = {
        processAbilities: arr?.filter(item=>item.isClear !== 'inspect'),
        audioUrl: uploadResult.ossUrl,
      }
      await InterviewCourseStore.postFollowUpSituation(route.query.nodeId, data)
    }
  } catch (error) {
    console.error('[FloatingBall-追问切片] 上传切片失败:', error);
  }
};

// 60s上传考察维度的完成情况并调用考察维度的完成情况接口
const uploadSliceForAssessmentSubPointCompletion = async (blob) => {
  if (!blob) {
    console.error("[FloatingBall-考察维度的完成情况] 没有可上传的录音切片");
    return;
  }
  try {
    const timestamp = Date.now();
    console.log('[FloatingBall-追问切片] 开始上传切片到OSS，大小:', blob.size, '字节');
    const ext = getFileExtension(blob.type || '');
    
    // 使用 audio-store 的上传方法，传入自定义文件名
    const uploadResult = await AudioStore._uploadToOSS({
      blob,
      processId: route.query.interviewId,
      nodeId: route.query.nodeId,
      interviewStore: InterviewStore,
      customFileName: `adviseRecording_${timestamp}.${ext}`
    });
    
    if (uploadResult.success) {
      console.log('[FloatingBall-追问切片] 切片上传成功，URL:', uploadResult.ossUrl);
      
      // 调用追问建议接口（不等待返回）
      const data = {
        audioUrl: uploadResult.ossUrl,
      }
      await InterviewCourseStore.postAssessmentSubPointCompletion(route.query.nodeId, data)
    }
  } catch (error) {
    console.error('[FloatingBall-追问切片] 上传切片失败:', error);
  }
}

/**
 * 生成点考察点的子考察点
 */
const postAssessmentProcessSubPointGenerateMain = async () => {
  const data = {
    id: InterviewCourseStore.mitNodeData?.id,
  }
  const res = await InterviewCourseStore.postAssessmentProcessSubPointGenerate(data);
  if (res.code === 200) {
    console.log("[FloatingBall] 生成考察子项:", res.data);
  }
}
/**
 * 轮询节点考察点的子考察点有没有生成
 */
const pollAssessmentProcessSubPointGeneratingStatus = async () => {
  const res = await InterviewCourseStore.getAssessmentProcessSubPointGeneratingStatus(route.query?.nodeId);
  if (res.code === 200) {
    console.log("[FloatingBall] 节点考察点的子考察点有没有生成:", res.data);
    if (res?.data) {
      const resNodeData = await InterviewCourseStore.getBusinessProcessNodeData(route.query?.nodeId);
      if (resNodeData.code === 200) {
        if (InterviewCourseStore.mitNodeData?.abilities[0]?.assessmentSubPoints?.length > 0) {
          InterviewCourseStore.suggestAbilityList = InterviewCourseStore.mitNodeData.abilities.map(item => {
              return {
                  ...item,
                  isClear: 'uninspect', // progress: 进行中，inspect: 已考察，uninspect: 未考察
              }
          });
          stopPollAssessmentProcessSubPointGeneratingStatus()
        } else {
          await postAssessmentProcessSubPointGenerateMain()
        }
      }
    }
  } else {
    console.log('轮询节点考察点的子考察点有没有生成失败');
    stopPollAssessmentProcessSubPointGeneratingStatus()
  }
}

/**
 * 开始轮询
 */
let pollAssessmentProcessSubPointGeneratingStatusTimer = null;
const startPollAssessmentProcessSubPointGeneratingStatus = () => {
  InterviewCourseStore.isAssessmentProcessSubPointGeneratingLoading = true;
  pollAssessmentProcessSubPointGeneratingStatusTimer = setInterval(() => {
    pollAssessmentProcessSubPointGeneratingStatus();
  }, 1000);
}
/**
 * 停止轮询
 */
const stopPollAssessmentProcessSubPointGeneratingStatus = () => {
  if (pollAssessmentProcessSubPointGeneratingStatusTimer) {
    clearInterval(pollAssessmentProcessSubPointGeneratingStatusTimer);
    pollAssessmentProcessSubPointGeneratingStatusTimer = null;
    InterviewCourseStore.isAssessmentProcessSubPointGeneratingLoading = false;
  }
}

// 监听录音状态变化
watch(() => InterviewCourseStore.currentNodeStatus, (newValue, oldValue) => {
  console.log("[FloatingBall] 录音状态变化:", oldValue, "->", newValue);
  
  // 清除之前的定时器
  if (sliceTimer) {
    clearInterval(sliceTimer);
    sliceTimer = null;
  }
  if (assessmentTimer) {
    clearInterval(assessmentTimer);
    assessmentTimer = null;
  }
  
  if (newValue === "recording" && oldValue !== "paused") {
    // 如果是从非暂停状态进入录音状态（即新开始录音），清空之前的数据
    InterviewCourseStore.suggestQuestionList = [];
    InterviewCourseStore.historyQuestionList = [];
    // 启动第一个追问切片录音（0-30s）
    startSliceRecording();
    // 启动第一个考察维度切片录音（0-60s）
    startSliceRecording60s();
    // 开始轮询节点考察点的子考察点有没有生成
    startPollAssessmentProcessSubPointGeneratingStatus();
    
    // 每30秒生成一个时间段切片：0-30s, 30-60s, 60-90s...
    sliceTimer = setInterval(() => {
      console.log("[FloatingBall] 追问切片定时器触发（30秒）");
      if (InterviewCourseStore.currentNodeStatus === 'recording' && AudioStore.isRecording && !AudioStore.isPaused) {
        // 停止当前切片（会触发stop事件，自动上传）
        stopSliceRecording();
        // 立即开始下一个30秒切片
        setTimeout(() => {
          startSliceRecording();
        }, 100); // 短暂延迟，确保上一个切片完全停止
      }
    }, 30000); // 30秒
    
    // 每60秒上传考察维度的完成情况
    assessmentTimer = setInterval(() => {
      console.log("[FloatingBall] 考察维度完成情况定时器触发（60秒）");
      if (InterviewCourseStore.currentNodeStatus === 'recording' && AudioStore.isRecording && !AudioStore.isPaused) {
        // 停止当前60秒切片（会触发stop事件，自动上传）
        stopSliceRecording60s();
        // 立即开始下一个60秒切片
        setTimeout(() => {
          startSliceRecording60s();
        }, 100); // 短暂延迟，确保上一个切片完全停止
      }
    }, 60000); // 60秒
    
  } else if (newValue === "recording" && oldValue === "paused") {
    // 如果是从暂停状态恢复录音
    // 注意：恢复后从当前时间点开始新的切片，而不是继续之前的切片
    // 恢复追问切片录音
    startSliceRecording();
    // 恢复考察维度切片录音
    startSliceRecording60s();
    
    // 每30秒生成一个时间段切片
    sliceTimer = setInterval(() => {
      console.log("[FloatingBall] 追问切片定时器触发（30秒）");
      if (InterviewCourseStore.currentNodeStatus === 'recording' && AudioStore.isRecording && !AudioStore.isPaused) {
        // 停止当前切片（会触发stop事件，自动上传）
        stopSliceRecording();
        // 立即开始下一个30秒切片
        setTimeout(() => {
          startSliceRecording();
        }, 100); // 短暂延迟，确保上一个切片完全停止
      }
    }, 30000); // 30秒
    
    // 每60秒上传考察维度的完成情况
    assessmentTimer = setInterval(() => {
      console.log("[FloatingBall] 考察维度完成情况定时器触发（60秒）");
      if (InterviewCourseStore.currentNodeStatus === 'recording' && AudioStore.isRecording && !AudioStore.isPaused) {
        // 停止当前60秒切片（会触发stop事件，自动上传）
        stopSliceRecording60s();
        // 立即开始下一个60秒切片
        setTimeout(() => {
          startSliceRecording60s();
        }, 100); // 短暂延迟，确保上一个切片完全停止
      }
    }, 60000); // 60秒
    
  } else if (newValue === "paused") {
    // 暂停时停止所有切片录音
    stopSliceRecording();
    stopSliceRecording60s();
    
  } else {
    // 停止时停止所有切片录音
    stopSliceRecording();
    stopSliceRecording60s();
  }
}, { deep: true });

// 监听主录音状态变化，同步切片录音（确保切片录音跟随主录音的暂停/恢复）
watch(
  () => [AudioStore.isRecording, AudioStore.isPaused],
  ([isRecording, isPaused]) => {
    // 如果主录音暂停或停止，停止切片录音
    if (!isRecording || isPaused) {
      if (sliceMediaRecorder.value && sliceMediaRecorder.value.state === 'recording') {
        console.log("[FloatingBall] 主录音暂停/停止，同步停止30秒切片录音");
        stopSliceRecording();
      }
      if (sliceMediaRecorder60s.value && sliceMediaRecorder60s.value.state === 'recording') {
        console.log("[FloatingBall] 主录音暂停/停止，同步停止60秒切片录音");
        stopSliceRecording60s();
      }
    }
  }
);

// 组件卸载时清理资源
onUnmounted(() => {
  // 清除追问切片定时器
  if (sliceTimer) {
    clearInterval(sliceTimer);
    sliceTimer = null;
  }
  
  // 清除考察维度完成情况定时器
  if (assessmentTimer) {
    clearInterval(assessmentTimer);
    assessmentTimer = null;
  }
  
  // 停止所有切片录音
  stopSliceRecording();
  stopSliceRecording60s();
});
</script>

<style scoped>
/* 样式 */
</style>